import { useQuery } from '@tanstack/react-query';

import { http } from '@/shared/api/httpClient';
import { unwrapEnvelopeData } from '@/shared/api/apiEnvelope';
import { env } from '@/shared/config/env';

import type { Store, StoreDetail, StoresListParams, StoresListResult } from '../types';

import { storeKeys } from './keys';
import { mapRemoteStoreDetail, mapRemoteStoreRow } from './mapRemoteStore';
import { parseStoresPagedFromUnwrappedData, type RemoteStoreDto } from './remoteStoreTypes';

function pickStoreDtoFromDetailBody(body: unknown): RemoteStoreDto | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  if (typeof o.id === 'string') return o as RemoteStoreDto;
  const items = o.items;
  if (Array.isArray(items) && items[0] && typeof items[0] === 'object') {
    return items[0] as RemoteStoreDto;
  }
  return null;
}

function clampPositiveInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.floor(value);
    return n > 0 ? n : fallback;
  }
  return fallback;
}

function buildListParams(params: StoresListParams | undefined): StoresListParams {
  const search = params?.search?.trim();
  return {
    page: params?.page,
    limit: params?.limit,
    search: search && search.length > 0 ? search : undefined,
  };
}

export async function fetchStoresList(params?: StoresListParams): Promise<StoresListResult> {
  const q = buildListParams(params);
  const res = await http.get<unknown>(env.adminDarkstoresPath, {
    params: {
      page: q.page ?? 1,
      limit: q.limit ?? 20,
      ...(q.search ? { search: q.search } : {}),
    },
  });
  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  const paged = parseStoresPagedFromUnwrappedData(unwrapped);

  const items = paged.items.map(mapRemoteStoreRow).filter((row): row is Store => row !== null);

  const page = clampPositiveInt(paged.page, q.page ?? 1);
  const limit = clampPositiveInt(paged.limit, q.limit ?? 20);

  const total =
    typeof paged.total === 'number' && Number.isFinite(paged.total) ? paged.total : items.length;
  const computedPageCount = Math.max(1, Math.ceil(total / limit));
  const pageCount =
    typeof paged.pageCount === 'number' && Number.isFinite(paged.pageCount) && paged.pageCount > 0
      ? paged.pageCount
      : computedPageCount;

  return {
    items,
    total,
    page,
    limit,
    pageCount,
  };
}

export async function fetchStoreDetail(storeId: string): Promise<StoreDetail> {
  const base = env.adminDarkstoresPath.replace(/\/$/, '');
  const res = await http.get<unknown>(`${base}/${encodeURIComponent(storeId)}`);
  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  const dto = pickStoreDtoFromDetailBody(unwrapped);
  const detail = dto
    ? mapRemoteStoreDetail(dto)
    : mapRemoteStoreDetail(unwrapped as RemoteStoreDto);
  if (!detail) {
    throw new Error('Invalid store response');
  }
  return detail;
}

export function useStoresQuery(options?: { enabled?: boolean; params?: StoresListParams }) {
  const params = buildListParams(options?.params);
  return useQuery({
    queryKey: storeKeys.list(params),
    queryFn: () => fetchStoresList(params),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useStoresStatsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: storeKeys.stats(),
    queryFn: () => fetchStoresList({ page: 1, limit: 100 }),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    retry: 1,
  });
}

export type StoresStatsQueryResult = ReturnType<typeof useStoresStatsQuery>;

export function useStoreDetailQuery(storeId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: storeKeys.detail(storeId),
    queryFn: () => fetchStoreDetail(storeId as string),
    enabled: Boolean(storeId) && (options?.enabled ?? true),
    staleTime: 30_000,
    retry: 1,
  });
}
